<template>
  <div
    class="column"
    data-test-id="title-plan-list"
  >
    <div class="col row justify-center q-gutter-lg q-mt-xs items-center">
      <div
        v-for="(bookingWeekMovies, planElementName) in bookingWeek.planElementNameToBookingWeekMoviesMap"
        :key="planElementName"
        class="col-auto column q-mt-xs q-mr-sm"
      >
        <div
          class="col-auto row q-gutter-x-xs"
        >
          <div
            class="col-auto q-my-auto text-overline"
            style="line-height: 10px; font-size: 10px;"
          >
            {{ planElementName }}
          </div>
          <showing-badge
            v-for="showingBadge in getPlanElement(planElementName).showingBadges.data"
            :key="`${planElementName}-${showingBadge.id}`"
            style="max-width: 50px; text-overflow: ellipsis; white-space: nowrap; overflow: hidden;"
            :showing-badge="showingBadge"
            size="xs"
            outline
          />
        </div>
        <div
          class="col-auto row items-center q-ma-none"
          style="margin-left: -8px;"
        >
          <title-slot
            v-for="bookingWeekMovie in bookingWeekMovies"
            :key="bookingWeekMovie.id"
            :booking-week-movie="bookingWeekMovie"
            @drag-start="dragStart"
            @drag-end="dragTo(bookingWeekMovie)"
            @title-double-click="edit(bookingWeekMovie)"
            @title-click="selector ? handleClick(bookingWeekMovie) : null"
          />
        </div>
      </div>
      <q-icon
        name="delete"
        color="grey"
        class="q-pa-lg q-mt-xs"
        style="transition: all 0.2s;"
        :style="{ 'font-size': dragOverDelete ? '100px' : '36px' }"
        @drop="dragOverDelete = false; dragTo(null)"
        @ondragover="event => bookingWeek.canRemoveMovie(dragStartItem.movie || dragStartItem) && event.preventDefault()"
        @dragover.prevent="dragOverDelete = true"
        @dragenter="dragOverDelete = true"
        @dragleave="dragOverDelete = false"
      />
    </div>
    <div class="q-mt-xl">
      <div>
        <div
          class="heading row items-center justify-center q-mb-md"
          style="position: relative"
        >
          <q-input
            v-model="search"
            :label="$t('search.for.titleGenreActorDate')"
            dense
            outlined
            class="q-mr-md"
            debounce="300"
          >
            <template #append>
              <q-icon
                v-if="search === ''"
                name="search"
              />
              <q-icon
                v-else
                name="clear"
                class="cursor-pointer"
                @click="search = ''"
              />
            </template>
          </q-input>
        </div>

        <div
          style="max-height: 400px;"
          class="scroll"
        >
          <div
            v-if="movies.data.length == 0"
            class="message"
          >
            No showings
          </div>

          <!-- We have a showtimes-->
          <div
            class="main-display"
          >
            <q-inner-loading v-show="movies.loading" />
            <div
              name="list-complete"
              class="row full-width content-stretch"
            >
              <div class="col row justify-center q-gutter-md items-center">
                <q-img
                  v-for="movie in movies.data"
                  :key="movie.id"
                  basic
                  class="poster col-auto"
                  :class="{ planned: planned(movie) }"
                  draggable
                  :src="movie.posterImageUrl"
                  @dragstart="dragStartItem = movie"
                >
                  <q-tooltip>
                    <div class="text-h6">
                      {{ movie.name }}
                    </div>
                    <div
                      v-if="movie.releaseDate"
                      class="text-caption"
                    >
                      {{ movie.releaseDate.i18n('date') }}
                    </div>
                    <showing-badge
                      v-for="showingBadge in movie.showingBadges.data"
                      :key="showingBadge.id"
                      :showing-badge="showingBadge"
                      solid
                    />
                  </q-tooltip>
                </q-img>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <movie-form ref="movieForm" />
  </div>
</template>

<script>

import { date } from 'quasar'
import TitleSlot from 'src/modules/admin/booking-week/components/TitleSlot'
import MovieForm from 'src/modules/admin/movie/MovieForm'
import ShowingBadge from 'src/modules/admin/showing-badge/components/ShowingBadge'

const { formatDate } = date

export default {
  name: 'TitlePlanList',
  components: {
    TitleSlot,
    MovieForm,
    ShowingBadge
  },
  props: {
    bookingWeek: {
      type: Object,
      required: true
    },
    selector: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['select'],
  data () {
    return {
      dragOverDelete: false,
      dragStartItem: null,
      dragEndBookingWeekMovie: null,
      formatDate,
      search: ''
    }
  },
  models: {
    movies: {
      marathonMovies: 'default',
      showingBadges: 'default',
      variables () {
        return {
          searchString: this.search
        }
      }
    }
  },
  computed: {
    holdOverMovies () {
      return _.map(this.bookingWeek.planElements.data[0].bookingWeekMovies.data, 'movie')
    },
    plannedMovieIds () {
      return _(this.bookingWeek.planElements.data).map('bookingWeekMovies.data').flatten().map('movie.id').value()
    }
  },
  methods: {
    dragStart (bookingWeekMovie) {
      this.dragStartItem = bookingWeekMovie
    },
    dragTo (dragEndBookingWeekMovie) {
      if (this.dragStartItem === null) return

      this.dragEndBookingWeekMovie = dragEndBookingWeekMovie

      if (this.dragEndBookingWeekMovie === null) {
        this.removeMovie(this.dragStartItem?.movie || this.dragStartItem, this.dragStartItem?.planElement?.name)
      } else if (this.dragStartItem.constructor.name === 'BookingWeekMovie') {
        // Swap
        this.swapMovies()
      } else if (this.dragStartItem.constructor.name === 'Movie') {
        // Set
        this.setMovie()
      } else {
        throw new Error('Unhandled type in drop')
      }
    },
    swapMovies () {
      if (this.dragStartItem.movie && this.dragStartItem.movie.id === this.dragEndBookingWeekMovie.movie && this.dragEndBookingWeekMovie.movie.id) {
        return
      }

      // Make sure there isn't a matching movie for the same element
      if (!this.dragEndBookingWeekMovie.movie && !this.bookingWeek.canRemoveMovie(this.dragStartItem.movie, this.dragEndBookingWeekMovie?.planElement?.name)) {
        return
      }

      const startMovie = this.dragStartItem.movie
      this.dragStartItem.movie = this.dragEndBookingWeekMovie.movie
      this.dragEndBookingWeekMovie.movie = startMovie

      const originalBy = this.bookingWeek.by
      const originalDayFilter = this.bookingWeek.dayFilter

      this.bookingWeek.by = 'day'
      this.bookingWeek.dayFilter = null
      _.each(this.bookingWeek.displaySchedule, showingSlot => {
        if (!showingSlot?.bookingWeekMovie || !showingSlot?.showing) return

        if (showingSlot.bookingWeekMovie.id === this.dragStartItem.id) {
          showingSlot.bookingWeekMovie = this.dragEndBookingWeekMovie
          showingSlot.showing.bookingWeekMovie = this.dragEndBookingWeekMovie
        } else if (showingSlot.bookingWeekMovie.id === this.dragEndBookingWeekMovie.id) {
          showingSlot.bookingWeekMovie = this.dragStartItem
          showingSlot.showing.bookingWeekMovie = this.dragStartItem
        }
      })
      this.bookingWeek.by = originalBy
      this.bookingWeek.dayFilter = originalDayFilter
    },
    setMovie () {
      const isMovieDestinationClear = !this.dragEndBookingWeekMovie.movie || this.removeMovie(this.dragEndBookingWeekMovie.movie, this.dragEndBookingWeekMovie.planElement.name)
      if (!isMovieDestinationClear) {
        return
      }

      const isMovieSourceUnduplicated = this.removeMovie(this.dragStartItem, this.dragEndBookingWeekMovie.planElement.name)
      if (!isMovieSourceUnduplicated) {
        return
      }

      this.dragEndBookingWeekMovie.movie = this.dragStartItem
    },
    removeMovie (movie, planElementName = null) {
      if (!movie || !this.bookingWeek.canRemoveMovie(movie, planElementName)) {
        return false
      }

      if (planElementName !== null) {
        // Clear all occurences of this movie for this plan element type
        _.each(_.filter(this.bookingWeek.planElements.data, { name: planElementName }), planElement => {
          _.each(planElement.bookingWeekMovies.data, bookingWeekMovie => {
            if (bookingWeekMovie.movie && bookingWeekMovie.movie.id === movie.id) {
              bookingWeekMovie.movie = null
            }
          })
        })
      } else {
        // Clear all occurrences of this movie in any bookingWeekMovie
        _.each(this.bookingWeek.planElements.data, planElement => {
          _.each(planElement.bookingWeekMovies.data, bookingWeekMovie => {
            if (bookingWeekMovie.movie && bookingWeekMovie.movie.id === movie.id) {
              bookingWeekMovie.movie = null
            }
          })
        })
      }

      return true
    },
    planned (movie) {
      return _.includes(this.plannedMovieIds, movie?.id)
    },
    edit (bookingWeekMovie) {
      if (!bookingWeekMovie.movie) {
        return
      }
      this.$refs.movieForm.show(bookingWeekMovie.movie)
    },
    handleClick (bookingWeekMovie) {
      this.$emit('select', bookingWeekMovie)
    },
    getPlanElement (name) {
      return _.find(this.bookingWeek.planElements.data, { name })
    }
  }
}
</script>

<style lang="scss" scoped>
  .poster {
    width: 100px;
    height: 145px;
    transition: all 0.1s;
  }
  .planned {
    opacity: 0.5;
  }

.release-date:nth-child(odd) {
  background-color:rgba(150, 150, 150, 0.2);
}

.release-date:nth-child(even) {
  background-color:rgba(150, 150, 150, 0.1);
}

</style>
